import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, MessageCircle, Phone, Mail } from 'lucide-react';
import { LangSwitch, Logo } from '../../../../components';
import { useLang, type Lang } from '../../../../i18n';
import { get } from '../../../../services/api';
import './Footer.scss';

const TELEGRAM = 'https://t.me/mrbek112';
const WHATSAPP = 'https://wa.me/998900770302';
const PHONE = '+998 90 077 03 02';
const PHONE_TEL = 'tel:+998900770302';
const EMAIL = 'info@ukam.uz';

const LANG_MAP: Record<Lang, string> = { 'uz-Latn': 'uz_lat', 'uz-Cyrl': 'uz_cyr', ru: 'ru', en: 'en' };

interface FooterCat { slug: string; title: string }

export default function Footer() {
  const { t, lang } = useLang();
  const [services, setServices] = useState<{ label: string; to: string }[]>([]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await get<{ results?: FooterCat[]; items?: FooterCat[] }>(`/services/categories/?lang=${LANG_MAP[lang]}`);
        if (cancel) return;
        const list = (res?.results || res?.items || []).slice(0, 7);
        setServices(list.map((c) => ({ label: c.title, to: `/services?category=${c.slug}` })));
      } catch {
        if (!cancel) setServices([]);
      }
    })();
    return () => { cancel = true; };
  }, [lang]);

  const company = [
    { label: t('nav.about'), to: '/about' },
    { label: t('nav.blog'), to: '/blog' },
    { label: t('footer.partners'), to: '/partners' },
    { label: t('nav.contacts'), to: '/contacts' },
  ];

  const legal = [
    { label: t('footer.privacy'), to: '/legal/privacy' },
    { label: t('footer.terms'), to: '/legal/terms' },
    { label: t('footer.cookies'), to: '/legal/cookies' },
    { label: t('footer.security'), to: '/legal/security' },
  ];

  return (
    <footer className="footer">
      <div className="container footer__container">
        <div className="footer__top">
          <div className="footer__brand">
            <Logo variant="white-mark" size={72} ariaLabel={t('brand.name')} className="footer__logo" />
            <p className="footer__desc">{t('brand.desc')}</p>
            <div className="footer__socials">
              <a className="footer__social footer__social--tg" href={TELEGRAM} target="_blank" rel="noreferrer noopener">
                <Send size={16} />
                <span>Telegram</span>
              </a>
              <a className="footer__social footer__social--wa" href={WHATSAPP} target="_blank" rel="noreferrer noopener">
                <MessageCircle size={16} />
                <span>WhatsApp</span>
              </a>
            </div>
          </div>

          <nav className="footer__nav" aria-label="Footer">
            <div className="footer__col">
              <h4 className="footer__title">{t('footer.services')}</h4>
              <ul className="footer__list">
                {services.length === 0 ? (
                  <li><Link to="/services" className="footer__link">{t('nav.services')}</Link></li>
                ) : services.map((s) => (
                  <li key={s.to}><Link to={s.to} className="footer__link">{s.label}</Link></li>
                ))}
              </ul>
            </div>
            <div className="footer__col">
              <h4 className="footer__title">{t('footer.company')}</h4>
              <ul className="footer__list">
                {company.map((s) => (
                  <li key={s.to}><Link to={s.to} className="footer__link">{s.label}</Link></li>
                ))}
              </ul>
            </div>
            <div className="footer__col">
              <h4 className="footer__title">{t('footer.contacts')}</h4>
              <ul className="footer__list footer__list--contacts">
                <li>
                  <a className="footer__contact" href={TELEGRAM} target="_blank" rel="noreferrer noopener">
                    <span className="footer__contact-icon footer__contact-icon--tg"><Send size={14} /></span>
                    <span>@mrbek112</span>
                  </a>
                </li>
                <li>
                  <a className="footer__contact" href={WHATSAPP} target="_blank" rel="noreferrer noopener">
                    <span className="footer__contact-icon footer__contact-icon--wa"><MessageCircle size={14} /></span>
                    <span>{PHONE}</span>
                  </a>
                </li>
                <li>
                  <a className="footer__contact" href={PHONE_TEL}>
                    <span className="footer__contact-icon footer__contact-icon--phone"><Phone size={14} /></span>
                    <span>{PHONE}</span>
                  </a>
                </li>
                <li>
                  <a className="footer__contact" href={`mailto:${EMAIL}`}>
                    <span className="footer__contact-icon footer__contact-icon--mail"><Mail size={14} /></span>
                    <span>{EMAIL}</span>
                  </a>
                </li>
              </ul>
            </div>
          </nav>
        </div>

        <div className="footer__bottom">
          <span className="footer__copy">{t('footer.copyright')}</span>
          <nav className="footer__legal" aria-label="Legal">
            {legal.map((l) => (
              <Link key={l.to} to={l.to} className="footer__link">{l.label}</Link>
            ))}
          </nav>
          <LangSwitch direction="up" />
        </div>
      </div>
    </footer>
  );
}
